import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import Canvas from './components/Canvas';
import Sidebar from './components/Sidebar';
import RunPanel from './components/RunPanel';
import NodeEditor from './components/NodeEditor';
import LogsPanel from './components/LogsPanel';
import Settings from './components/Settings';
import TourOverlay, { type TourStep } from './components/TourOverlay';
import type { NodeTypeInfo, WorkflowDef, RunResult, NodeResult } from './types';
import {
  AUTH_UNAUTHORIZED_EVENT,
  apiUrl,
  authFetch,
  buildWsUrl,
  clearAuthToken,
  getAuthToken,
  setAuthToken,
} from './api';
import './App.css';

export default function App() {
  const [nodeTypes, setNodeTypes] = useState<Record<string, NodeTypeInfo>>({});
  const [workflow, setWorkflow] = useState<WorkflowDef | null>(null);
  const [workflows, setWorkflows] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [activeNodes, setActiveNodes] = useState<Record<string, NodeResult>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rightTab, setRightTab] = useState<'chat' | 'nodes' | 'logs'>('chat');
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [sidebarRequestedTab, setSidebarRequestedTab] = useState<'nodes' | 'info' | null>(null);

  const [authReady, setAuthReady] = useState(false);
  const [authUser, setAuthUser] = useState('');
  const [loginUsername, setLoginUsername] = useState('Uniuserone');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [appLoadError, setAppLoadError] = useState('');
  const workflowIdRef = useRef('');
  const runningWsRef = useRef<WebSocket | null>(null);
  const runningRunIdRef = useRef('');
  const cancelRequestedRef = useRef(false);
  const tourSteps = useRef<TourStep[]>([
    {
      selector: '[data-tour="app-header"]',
      title: 'Top Bar',
      description: 'Select workflows, reload data, open settings, or logout from here.',
      placement: 'bottom',
    },
    {
      selector: '[data-tour="sidebar"]',
      title: 'Left Sidebar',
      description: 'This is your builder toolbox and quick workflow controls.',
      placement: 'right',
    },
    {
      selector: '[data-tour="info-tab"]',
      title: 'Info Tab',
      description: 'Open Info to get a quick usage checklist and start this tour anytime.',
      placement: 'right',
    },
    {
      selector: '[data-tour="show-tour"]',
      title: 'Show Tour Button',
      description: 'Click this button anytime to replay the guided walkthrough.',
      placement: 'right',
    },
    {
      selector: '[data-tour="nodes-tab"]',
      title: 'Nodes Tab',
      description: 'Switch back to Nodes to access the draggable node palette.',
      placement: 'right',
    },
    {
      selector: '[data-tour="node-palette-item"]',
      title: 'Node Palette',
      description: 'Drag a node from here and drop it onto the canvas.',
      placement: 'right',
    },
    {
      selector: '[data-tour="canvas-area"]',
      title: 'Canvas',
      description: 'Build your flow here. Connect nodes and double-click edges to add conditions.',
      placement: 'left',
    },
    {
      selector: '[data-tour="right-tabs"]',
      title: 'Right Panel Tabs',
      description: 'Use these tabs to switch between Chat, Node Editor, and Logs.',
      placement: 'left',
    },
    {
      selector: '[data-tour="chat-input"]',
      title: 'Chat Runner',
      description: 'Ask a question and run your workflow. Use Stop to cancel active runs.',
      placement: 'left',
    },
    {
      selector: '[data-tour="nodes-panel-tab"]',
      title: 'Node Editor Tab',
      description: 'Select a node on the canvas, then edit prompt and config in this tab.',
      placement: 'left',
    },
    {
      selector: '[data-tour="logs-panel-tab"]',
      title: 'Logs Tab',
      description: 'Review run history, outcomes, and replay details in logs.',
      placement: 'left',
    },
  ]);

  const clearAppState = useCallback(() => {
    if (runningWsRef.current) {
      try {
        runningWsRef.current.close();
      } catch {
        // ignore
      }
    }
    runningWsRef.current = null;
    runningRunIdRef.current = '';
    cancelRequestedRef.current = false;
    setWorkflow(null);
    setWorkflows([]);
    setNodeTypes({});
    setRunResult(null);
    setActiveNodes({});
    setSelectedNode(null);
    setSettingsOpen(false);
    setIsRunning(false);
    setTourOpen(false);
    setTourStepIndex(0);
    setSidebarRequestedTab(null);
  }, []);

  const startTour = useCallback(() => {
    setTourStepIndex(0);
    setTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    setTourStepIndex(0);
    setSidebarRequestedTab(null);
  }, []);

  const nextTourStep = useCallback(() => {
    setTourStepIndex(prev => {
      if (prev >= tourSteps.current.length - 1) {
        setTourOpen(false);
        setSidebarRequestedTab(null);
        return 0;
      }
      return prev + 1;
    });
  }, []);

  const prevTourStep = useCallback(() => {
    setTourStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const logout = useCallback(
    (message = '') => {
      clearAuthToken();
      setAuthUser('');
      clearAppState();
      setLoginError(message);
    },
    [clearAppState],
  );

  const loadWorkflow = useCallback(async (id: string) => {
    const res = await authFetch(`/api/workflows/${id}`);
    if (!res.ok) {
      throw new Error(`Failed to load workflow (${res.status})`);
    }
    const wf = await res.json();
    setWorkflow(wf);
    setRunResult(null);
    setActiveNodes({});
    setSelectedNode(null);
  }, []);

  const loadWorkflowList = useCallback(async () => {
    const res = await authFetch('/api/workflows');
    if (!res.ok) {
      throw new Error(`Failed to load workflows (${res.status})`);
    }
    const list = await res.json();
    if (!Array.isArray(list)) {
      throw new Error('Invalid workflow list response');
    }
    setWorkflows(list);
    if (!workflowIdRef.current) {
      const defaultExists = Array.isArray(list) && list.some((w: any) => w?.id === 'default');
      const initialId = defaultExists ? 'default' : (list[0]?.id || '');
      if (initialId) {
        await loadWorkflow(initialId);
      }
    }
  }, [loadWorkflow]);

  const loadNodeTypes = useCallback(async () => {
    const res = await authFetch('/api/node-types');
    if (!res.ok) {
      throw new Error(`Failed to load node types (${res.status})`);
    }
    const data = await res.json();
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid node types response');
    }
    setNodeTypes(data);
  }, []);

  const bootstrapAppData = useCallback(async () => {
    setIsBootstrapping(true);
    setAppLoadError('');
    try {
      await loadNodeTypes();
      await loadWorkflowList();
    } catch (err: any) {
      const message = err?.message || 'Failed to load app data';
      setAppLoadError(message);
    }
    setIsBootstrapping(false);
  }, [loadNodeTypes, loadWorkflowList]);

  useEffect(() => {
    const onUnauthorized = () => {
      logout('Session expired. Please login again.');
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        setAuthReady(true);
        return;
      }
      try {
        const res = await authFetch('/api/auth/me');
        if (!res.ok) {
          logout('');
          setAuthReady(true);
          return;
        }
        const me = await res.json();
        setAuthUser(String(me.username || ''));
      } catch {
        logout('');
      }
      setAuthReady(true);
    };
    initAuth();
  }, [logout]);

  useEffect(() => {
    if (!authReady || !authUser) return;
    bootstrapAppData();
  }, [authReady, authUser, bootstrapAppData]);

  useEffect(() => {
    workflowIdRef.current = workflow?.id || '';
  }, [workflow?.id]);

  useEffect(() => {
    if (!tourOpen) {
      setSidebarRequestedTab(null);
      return;
    }

    const selector = tourSteps.current[tourStepIndex]?.selector;
    if (!selector) return;

    if (selector === '[data-tour="info-tab"]' || selector === '[data-tour="show-tour"]') {
      setSidebarRequestedTab('info');
    } else if (selector === '[data-tour="nodes-tab"]' || selector === '[data-tour="node-palette-item"]') {
      setSidebarRequestedTab('nodes');
    } else {
      setSidebarRequestedTab(null);
    }

    if (selector === '[data-tour="chat-input"]') {
      setRightTab('chat');
    } else if (selector === '[data-tour="nodes-panel-tab"]') {
      setRightTab('nodes');
    } else if (selector === '[data-tour="logs-panel-tab"]') {
      setRightTab('logs');
    }
  }, [tourOpen, tourStepIndex]);

  const saveWorkflow = useCallback(async (wf: WorkflowDef) => {
    setWorkflow(wf);
    authFetch(`/api/workflows/${wf.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(wf),
    }).catch(() => {});
  }, []);

  const runQuery = useCallback(async (
    query: string,
    conversation: Array<Record<string, any>> = [],
    followUpMode = false,
  ) => {
    if (!workflow) return;
    cancelRequestedRef.current = false;
    runningRunIdRef.current = '';
    setIsRunning(true);
    setRunResult(null);
    setActiveNodes({});

    if (runningWsRef.current) {
      try {
        runningWsRef.current.close();
      } catch {
        // ignore
      }
      runningWsRef.current = null;
    }

    const ws = new WebSocket(buildWsUrl('/ws/run'));
    runningWsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        query,
        workflow_id: workflow.id,
        conversation,
        follow_up_mode: followUpMode,
      }));
    };

    ws.onmessage = event => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'run_started') {
        runningRunIdRef.current = String(msg?.data?.run_id || '');
      } else if (msg.type === 'node_result') {
        setActiveNodes(prev => {
          const next = { ...prev };
          if (msg.data.status === 'completed') {
            next[msg.data.node_id] = msg.data;
          } else {
            delete next[msg.data.node_id];
          }
          return next;
        });
      } else if (msg.type === 'complete') {
        setRunResult(msg.data);
        setIsRunning(false);
        runningRunIdRef.current = '';
        runningWsRef.current = null;
        ws.close();
      } else if (msg.type === 'error') {
        setRunResult({
          run_id: runningRunIdRef.current || '',
          workflow_id: workflow.id,
          query,
          results: [],
          final_answer: `Error: ${msg.data}`,
          stopped_early: true,
          stop_reason: msg.data,
        });
        setIsRunning(false);
        runningRunIdRef.current = '';
        runningWsRef.current = null;
        ws.close();
      }
    };

    ws.onclose = event => {
      runningWsRef.current = null;
      runningRunIdRef.current = '';
      setIsRunning(false);
      if (event.code === 4401) {
        logout('Session expired. Please login again.');
      }
    };

    ws.onerror = () => {
      if (cancelRequestedRef.current) {
        setIsRunning(false);
        return;
      }
      authFetch('/api/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          workflow_id: workflow.id,
          conversation,
          follow_up_mode: followUpMode,
        }),
      })
        .then(async r => {
          if (!r.ok) throw new Error(`Run failed (${r.status})`);
          return r.json();
        })
        .then(data => {
          setRunResult(data);
          const nodeMap: Record<string, NodeResult> = {};
          data.results.forEach((nr: NodeResult) => {
            if (nr.status === 'completed') {
              nodeMap[nr.node_id] = nr;
            }
          });
          setActiveNodes(nodeMap);
        })
        .catch(err => {
          setRunResult({
            run_id: runningRunIdRef.current || '',
            workflow_id: workflow.id,
            query,
            results: [],
            final_answer: `Error: ${err.message}`,
            stopped_early: true,
            stop_reason: err.message,
          });
        })
        .finally(() => {
          setIsRunning(false);
          runningWsRef.current = null;
          runningRunIdRef.current = '';
        });
    };
  }, [workflow, logout]);

  const cancelRun = useCallback(async () => {
    cancelRequestedRef.current = true;
    const runId = runningRunIdRef.current;
    if (runId) {
      await authFetch(`/api/runs/${runId}/cancel`, { method: 'POST' }).catch(() => {});
    }
    if (runningWsRef.current) {
      try {
        runningWsRef.current.close(4000, 'Cancelled by user');
      } catch {
        // ignore
      }
      runningWsRef.current = null;
    }
    setIsRunning(false);
  }, []);

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const username = loginUsername.trim();
      const password = loginPassword;
      if (!username || !password || isLoggingIn) return;

      setIsLoggingIn(true);
      setLoginError('');
      try {
        const res = await fetch(apiUrl('/api/auth/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => null);
          const detail = String(payload?.detail || `Login failed (${res.status})`);
          throw new Error(detail);
        }
        const payload = await res.json();
        const token = String(payload.token || '');
        const loggedInUser = String(payload.username || username);
        if (!token) {
          throw new Error('No token returned by server');
        }
        setAuthToken(token);
        setAuthUser(loggedInUser);
        setLoginPassword('');
      } catch (err: any) {
        setLoginError(err?.message || 'Login failed');
      }
      setIsLoggingIn(false);
    },
    [loginUsername, loginPassword, isLoggingIn],
  );

  const selectedNodeData = workflow?.nodes.find(n => n.id === selectedNode) || null;

  if (!authReady) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h2>Agentic Workflow Builder</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="auth-screen">
        <form className="auth-card" onSubmit={handleLogin}>
          <h2>Client Login</h2>
          <p>Use your assigned username and password.</p>

          <label>Username</label>
          <input
            value={loginUsername}
            onChange={e => setLoginUsername(e.target.value)}
            autoComplete="username"
            disabled={isLoggingIn}
          />

          <label>Password</label>
          <input
            type="password"
            value={loginPassword}
            onChange={e => setLoginPassword(e.target.value)}
            autoComplete="current-password"
            disabled={isLoggingIn}
          />

          {loginError && <div className="settings-status error">{loginError}</div>}

          <button className="btn-primary" type="submit" disabled={isLoggingIn}>
            {isLoggingIn ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header" data-tour="app-header">
        <h1>Agentic Workflow Builder</h1>
        <div className="header-right">
          <div className="workflow-selector" data-tour="workflow-selector">
            <select value={workflow?.id || ''} onChange={e => loadWorkflow(e.target.value)}>
              {workflows.map(w => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
          <button className="settings-btn" onClick={() => bootstrapAppData()}>
            Reload Data
          </button>
          <button className="settings-btn" onClick={() => setSettingsOpen(true)}>
            Settings
          </button>
          <button className="settings-btn" onClick={() => logout('')}>
            Logout ({authUser})
          </button>
        </div>
      </header>

      <div className="app-body">
        <Sidebar
          nodeTypes={nodeTypes}
          workflow={workflow}
          onSave={saveWorkflow}
          onShowTour={startTour}
          requestedTab={sidebarRequestedTab}
        />

        <div className="canvas-area" data-tour="canvas-area">
          {appLoadError && (
            <div className="panel-placeholder" style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(15,23,42,0.82)' }}>
              <div>
                Failed loading workflows/node types: {appLoadError}
                <br />
                <button className="btn-secondary" style={{ marginTop: 10 }} onClick={() => bootstrapAppData()}>
                  Try Again
                </button>
              </div>
            </div>
          )}
          {!appLoadError && isBootstrapping && (
            <div className="panel-placeholder" style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(15,23,42,0.72)' }}>
              Loading workflows...
            </div>
          )}
          <ReactFlowProvider>
            <Canvas
              workflow={workflow}
              nodeTypes={nodeTypes}
              activeNodes={activeNodes}
              onWorkflowChange={saveWorkflow}
              onNodeSelect={id => {
                setSelectedNode(id);
                if (id) setRightTab('nodes');
              }}
            />
          </ReactFlowProvider>
        </div>

        <div className="right-panel">
          <div className="right-tabs" data-tour="right-tabs">
            <button
              className={`right-tab ${rightTab === 'chat' ? 'active' : ''}`}
              onClick={() => setRightTab('chat')}
              data-tour="chat-panel-tab"
            >
              Chat
            </button>
            <button
              className={`right-tab ${rightTab === 'nodes' ? 'active' : ''}`}
              onClick={() => setRightTab('nodes')}
              data-tour="nodes-panel-tab"
            >
              Node Editor
            </button>
            <button
              className={`right-tab ${rightTab === 'logs' ? 'active' : ''}`}
              onClick={() => setRightTab('logs')}
              data-tour="logs-panel-tab"
            >
              Logs
            </button>
          </div>

          {rightTab === 'chat' && (
            <RunPanel
              onRun={runQuery}
              onCancel={cancelRun}
              result={runResult}
              isRunning={isRunning}
              workflowId={workflow?.id || ''}
            />
          )}

          {rightTab === 'nodes' && (
            <>
              {selectedNodeData ? (
                <NodeEditor
                  node={selectedNodeData}
                  nodeTypeInfo={nodeTypes[selectedNodeData.type] || null}
                  workflow={workflow}
                  onWorkflowChange={saveWorkflow}
                  onChange={updated => {
                    if (!workflow) return;
                    const newNodes = workflow.nodes.map(n => (n.id === updated.id ? updated : n));
                    saveWorkflow({ ...workflow, nodes: newNodes });
                  }}
                  onClose={() => setSelectedNode(null)}
                />
              ) : (
                <div className="panel-placeholder">
                  Click a node on the canvas to edit its prompt and config
                </div>
              )}
            </>
          )}

          {rightTab === 'logs' && <LogsPanel workflowId={workflow?.id || ''} />}
        </div>
      </div>

      <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <TourOverlay
        open={tourOpen}
        steps={tourSteps.current}
        stepIndex={tourStepIndex}
        onClose={closeTour}
        onNext={nextTourStep}
        onPrev={prevTourStep}
      />
    </div>
  );
}
